/* eslint-disable max-lines */
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FeedbackStatus } from "../../interfaces/endpoints/ICustomerFeedback";
import { updateFeedbackStatus } from "../../services/CustomerFeedback.service";
import { cx } from "../../utils/classnames";
import { STATUS_OPTIONS } from "../../utils/customerFeedback/labels";
import Button from "../ui/Button";
import StatusBadge from "./StatusBadge";

interface UpdateStatusModalProps {
  show: boolean;
  feedbackId: number;
  currentStatus: FeedbackStatus;
  onClose: () => void;
  onUpdated: (newStatus: FeedbackStatus) => void;
}

const UpdateStatusModal: FC<UpdateStatusModalProps> = ({
  show,
  feedbackId,
  currentStatus,
  onClose,
  onUpdated
}) => {
  const [newStatus, setNewStatus] = useState<FeedbackStatus>(currentStatus);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) setNewStatus(currentStatus);
  }, [show, currentStatus]);

  useEffect(() => {
    if (!show) return;
    document.body.classList.add("overflow-hidden");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keyup", onKey);
    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keyup", onKey);
    };
  }, [show, onClose]);

  const handleSubmit = () => {
    if (newStatus === currentStatus) {
      onClose();
      return;
    }
    setSubmitting(true);
    updateFeedbackStatus(feedbackId, { status: newStatus })
      .then(() => {
        toast.success("Statusi u përditësua.", { position: "top-right" });
        onUpdated(newStatus);
        onClose();
      })
      .catch(() => {
        toast.error("Statusi nuk u përditësua.", { position: "top-right" });
      })
      .finally(() => setSubmitting(false));
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start
        justify-center z-50 p-4 pt-52"
    >
      <div
        className="bg-white rounded-lg w-full max-w-md flex flex-col
          shadow-xl"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-3
          border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            Ndrysho statusin
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Mbyll"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <fieldset className="space-y-2">
            <legend className="sr-only">Statusi</legend>
            {STATUS_OPTIONS.map((opt) => {
              const checked = newStatus === opt.value;
              return (
                <label
                  key={opt.value}
                  className={cx([
                    "flex items-center justify-between gap-3 rounded-lg",
                    "border px-3 py-2.5 cursor-pointer transition-colors",
                    checked
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 hover:bg-gray-50"
                  ])}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <input
                      type="radio"
                      name="feedback-status"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setNewStatus(opt.value)}
                      className="sr-only"
                    />
                    <span
                      aria-hidden
                      className={cx([
                        "w-4 h-4 rounded-full border flex items-center",
                        "justify-center shrink-0 transition-colors",
                        checked
                          ? "border-primary bg-primary"
                          : "border-gray-300 bg-white"
                      ])}
                    >
                      {checked && (
                        <CheckIcon
                          className="w-3 h-3 text-white"
                          strokeWidth={3}
                        />
                      )}
                    </span>
                    <span className="text-sm text-tableColor truncate">
                      {opt.label}
                    </span>
                  </span>
                  <StatusBadge status={opt.value} />
                </label>
              );
            })}
          </fieldset>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50
          rounded-b-lg">
          <div className="flex justify-end items-center gap-3">
            <Button
              onClick={onClose}
              btnType="custom"
              className="border w-28 border-gray-300 text-gray-700
                hover:bg-gray-100"
            >
              Anulo
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="w-28">
              {submitting ? "Duke ruajtur..." : "Ruaj"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateStatusModal;
